<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/logs/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.logs')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <log-changes-modal v-model:open="openChangesModal" :entity-change="currentEntityChange" />
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-click="rowClickFunction"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import { enumOptionsProvider, useProvider } from '@components/utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type { HydraCollection, EntityChange } from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import LogChangesModal from '@pages/users/LogChangesModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef('filter', {});

const sortKey = useQueryRef<string | undefined>('sortKey', 'date');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const openChangesModal = ref(false);
const currentEntityChange = ref();

const { t } = useI18n();

const rowClickFunction = function (item: EntityChange): void {
  currentEntityChange.value = item;
  openChangesModal.value = true;
};

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.users.logList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'className',
    title: t('entityChange.className'),
    sortable: true,
    visible: true,
    group: t('pages.users.logList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.users.logList.filterGroups.general'),
      options: {
        label: t('entityChange.className'),
        options: enumOptionsProvider('/logs/classes', (v) => ({
          label: v,
          value: v,
        })),
        searchable: true,
      },
    },
  },
  {
    key: 'entityId',
    title: t('entityChange.entityId'),
    sortable: false,
    visible: true,
    group: t('pages.users.logList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.users.logList.filterGroups.general'),
      options: {
        label: t('entityChange.entityId'),
      },
    },
  },
  {
    key: 'type',
    title: t('entityChange.type'),
    sortable: true,
    visible: true,
    group: t('pages.users.logList.fieldGroups.general'),
    filter: {
      type: 'dropdown',
      group: t('pages.users.logList.filterGroups.general'),
      options: {
        options: ['create', 'update', 'delete'],
        label: t('entityChange.type'),
      },
    },
  },
  {
    key: 'date',
    title: t('entityChange.date'),
    group: t('pages.users.logList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.users.logList.filterGroups.general'),
      options: {
        label: t('entityChange.date'),
      },
    },
  },
  {
    key: 'numberOfProperties',
    title: t('entityChange.numberOfProperties'),
    sortable: false,
    visible: true,
    computed: (item: EntityChange) => Object.keys(item.changes).length,
    group: t('pages.users.logList.fieldGroups.general'),
  },
  {
    key: 'user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.users.logList.fieldGroups.user'),
    filter: {
      type: 'text',
      group: t('pages.users.logList.filterGroups.user'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'user.lastName',
    title: t('user.lastName'),
    group: t('pages.users.logList.fieldGroups.user'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.users.logList.filterGroups.user'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'user.roles',
    csvTranslations: {
      ROLE_ADMIN: t('user.roles.ROLE_ADMIN'),
      ROLE_SUPER_ADMIN: t('user.roles.ROLE_SUPER_ADMIN'),
      ROLE_MONITORING: t('user.roles.ROLE_MONITORING'),
      ROLE_DRIVER: t('user.roles.ROLE_DRIVER'),
      ROLE_USER: t('user.roles.ROLE_USER'),
      ROLE_COMPANY_USER: t('user.roles.ROLE_COMPANY_USER'),
      ROLE_VERIFIER: t('user.roles.ROLE_VERIFIER'),
      ROLE_HELPER: t('user.roles.ROLE_HELPER'),
      ROLE_VISIBILITY: t('user.roles.ROLE_VISIBILITY'),
    },
    title: t('user.role'),
    group: t('pages.users.logList.fieldGroups.user'),
    sortable: false,
    visible: true,
    formatter: (v: string[]) =>
      v
        .filter((el) => el !== 'ROLE_USER')
        .map((item) => t(`user.roles.${item}`))
        .join(', '),
    filter: {
      type: 'dropdown',
      group: t('pages.users.logList.filterGroups.user'),
      options: {
        options: enumOptionsProvider('/enums/NonDriverRole', (v) => ({
          label: t(`user.roles.${v}`),
          value: v,
        })),
        label: t('user.role'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<EntityChange>>('/logs', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
